import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { REHYDRATE } from "redux-persist";
import Logger from "../../foundation/Logger";
import { unwrappedDispatch } from "../../foundation/Dispatching";

export class KnownSettings {
    [property: string | symbol]: any;

    data: Record<string, string>;

    private static indexedHandler: ProxyHandler<KnownSettings> = {
        get<T>(target: KnownSettings, property: string) {
            if (property === "data") return target.data;
            property = property[0].toUpperCase() + property.slice(1);
            try {
                const temp = target.data[property];
                if (temp === undefined) return undefined as T;
                return JSON.parse(target.data[property]) as T;
            } catch (e) {
                return target.data[property] as T;
            }
        },
        set<T>(target: KnownSettings, property: string, value: T) {
            property = property[0].toUpperCase() + property.slice(1);
            target.data[property] =
                typeof value === "string" ? value : JSON.stringify(value);
            return true;
        },
    };

    constructor(data: Record<string, string>) {
        this.data = { ...data };

        return new Proxy(this, KnownSettings.indexedHandler);
    }

    toJSON() {
        return undefined;
    }
}

// Define the type for our state store entry
type SettingsState = {
    settings: Record<string, string>;
    preferences: Record<string, string>;
    settingsStatus: string;
    preferencesStatus: string;
    error: string;
};

const initData: Record<string, string> = {};
// Define the initial state for our state store entry
const initialState: SettingsState = {
    settings: initData,
    preferences: initData,
    settingsStatus: "idle",
    preferencesStatus: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const settingSlice = createSlice({
    name: "setting",
    initialState: initialState,
    reducers: {
        updateSettingValue: (
            state,
            action: PayloadAction<UpdateSettingPayload>,
        ) => {
            state.settings = {
                ...state.settings,
                [action.payload.name]: action.payload.value,
            };
        },
        updateSettingAll: (
            state,
            action: PayloadAction<Record<string, string>>,
        ) => {
            state.settings = { ...state.settings, ...action.payload };
        },
        updatePreferenceValue: (
            state,
            action: PayloadAction<UpdateSettingPayload>,
        ) => {
            state.preferences = {
                ...state.preferences,
                [action.payload.name]: action.payload.value,
            };
        },
        updatePreferenceAll: (
            state,
            action: PayloadAction<Record<string, string>>,
        ) => {
            state.preferences = { ...state.preferences, ...action.payload };
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    settings: rawData?.payload?.settings?.settings ?? initData,
                    preferences:
                        rawData?.payload?.settings?.preferences ?? initData,
                    settingsStatus: !rawData?.payload?.settings?.settings
                        ? "idle"
                        : "succeeded",
                    preferencesStatus: !rawData?.payload?.settings?.preferences
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchSettings.pending, (state, action) => {
                state.settingsStatus = "loading";
            })
            .addCase(fetchSettings.fulfilled, (state, action) => {
                state.settingsStatus = "succeeded";
                // Add any fetched posts to the array
                state.settings = action.payload;
            })
            .addCase(fetchSettings.rejected, (state, action) => {
                state.settingsStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchPreferences.pending, (state, action) => {
                state.preferencesStatus = "loading";
            })
            .addCase(fetchPreferences.fulfilled, (state, action) => {
                state.preferencesStatus = "succeeded";
                // Add any fetched posts to the array
                state.preferences = action.payload;
            })
            .addCase(fetchPreferences.rejected, (state, action) => {
                state.preferencesStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const {
    updateSettingValue,
    updateSettingAll,
    updatePreferenceValue,
    updatePreferenceAll,
} = settingSlice.actions;

export const initialiseSettingsDetails = createAsyncThunk(
    "setting/initialiseSettingsDetails",
    (_, thunkAPI) => {
        return Promise.all([
            thunkAPI.dispatch(fetchSettings()),
            thunkAPI.dispatch(fetchPreferences()),
        ]);
    },
);
export const getSetting = async <T>(
    type: () => T,
    name: string,
): Promise<T> => {
    await store.dispatch(initialiseSettingsDetails());
    const state = store.getState() as IAppState;
    switch (typeof type) {
        case "boolean":
            return (state.db.settings.settings[name] ===
                "true") as unknown as T;
        case "number":
            return Number(state.db.settings.settings[name]) as unknown as T;
        case "object":
            if (type!() instanceof Date)
                return new Date(
                    state.db.settings.settings[name],
                ) as unknown as T;
            else {
                try {
                    const result = type!();
                    Object.assign(
                        result,
                        JSON.parse(state.db.settings.settings[name]),
                    );
                    return result;
                } catch (e) {
                    Logger.error(e);
                }
            }
            break;
        default:
            return state.db.settings.settings[name] as unknown as T;
    }
};
export const setSetting = async (
    name: string,
    value: any,
    encrypt: boolean = false,
): Promise<void> => {
    if (encrypt) {
        await store.dispatch(updateEncryptedSetting({ name, value }));
    } else {
        await store.dispatch(updateSetting({ name, value }));
    }
};
export const getKnownSettings = (): KnownSettings => {
    const state = store.getState() as IAppState;
    return new KnownSettings(state.db.settings.settings);
};
export const updateKnownSettings = async (settings: KnownSettings) => {
    const state = store.getState() as IAppState;
    const updated = {};
    for (const key in settings.data) {
        if (settings.data[key] !== state.db.settings.settings[key])
            updated[key] = settings.data[key];
    }
    await unwrappedDispatch(updateSettings(updated));
};
export const getPreference = async <T>(
    type: () => T,
    name: string,
): Promise<T> => {
    await store.dispatch(initialiseSettingsDetails());
    const state = store.getState() as IAppState;
    switch (typeof type) {
        case "boolean":
            return (state.db.settings.preferences[name] ===
                "true") as unknown as T;
        case "number":
            return Number(state.db.settings.preferences[name]) as unknown as T;
        case "object":
            if (type!() instanceof Date)
                return new Date(
                    state.db.settings.preferences[name],
                ) as unknown as T;
            else {
                try {
                    const result = type!();
                    Object.assign(
                        result,
                        JSON.parse(state.db.settings.preferences[name]),
                    );
                    return result;
                } catch (e) {
                    Logger.error(e);
                }
            }
            break;
        default:
            return state.db.settings.preferences[name] as unknown as T;
    }
};
export const setPreference = async (
    name: string,
    value: any,
): Promise<void> => {
    await store.dispatch(updatePreference({ name, value }));
};
export const getKnownPreferences = (): KnownSettings => {
    const state = store.getState() as IAppState;
    return new KnownSettings(state.db.settings.preferences);
};
export const updateKnownPreferences = async (settings: KnownSettings) => {
    const state = store.getState() as IAppState;
    const updated = {};
    for (const key in settings.data) {
        if (settings.data[key] !== state.db.settings.preferences[key])
            updated[key] = settings.data[key];
    }
    await unwrappedDispatch(updatePreferences(updated));
};

// This is a thunk, they are used to load the state
const fetchSettings = createAsyncThunk(
    "setting/fetchSettings",
    () => Datastore.getType<Record<string, string>>("api/Settings/All"),
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.settings.settingsStatus === "idle",
    },
);
const fetchPreferences = createAsyncThunk(
    "setting/fetchPreferences",
    () =>
        Datastore.getType<Record<string, string>>(
            "api/Settings/Preferences/All",
        ),
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.settings.preferencesStatus ===
            "idle",
    },
);

interface UpdateSettingPayload {
    name: string;
    value: string;
}
const updateSetting = createAsyncThunk(
    "setting/updateSetting",
    async (payload: UpdateSettingPayload, thunkAPI) => {
        await Datastore.post(`api/Settings/${payload.name}`, payload.value);
        thunkAPI.dispatch(updateSettingValue(payload));
    },
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.settings.settingsStatus ===
            "succeeded",
    },
);
const updateSettings = createAsyncThunk(
    "setting/updateSettings",
    async (payload: Record<string, string>, thunkAPI) => {
        await Datastore.post(`api/Settings/All`, payload);
        thunkAPI.dispatch(updateSettingAll(payload));
    },
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.settings.settingsStatus ===
            "succeeded",
    },
);
const updateEncryptedSetting = createAsyncThunk(
    "setting/updateEncryptedSetting",
    async (payload: UpdateSettingPayload, thunkAPI) => {
        await Datastore.post(
            `api/Settings/Encrypted/${payload.name}`,
            payload.value,
        );
        thunkAPI.dispatch(updateSettingValue(payload));
    },
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.settings.settingsStatus ===
            "succeeded",
    },
);
const updatePreference = createAsyncThunk(
    "setting/updatePreference",
    async (payload: UpdateSettingPayload, thunkAPI) => {
        await Datastore.post(
            `api/Settings/Preferences/${payload.name}`,
            payload.value,
        );
        thunkAPI.dispatch(updatePreferenceValue(payload));
    },
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.settings.preferencesStatus ===
            "succeeded",
    },
);
const updatePreferences = createAsyncThunk(
    "setting/updatePreferences",
    async (payload: Record<string, string>, thunkAPI) => {
        await Datastore.post(`api/Settings/Preferences/All`, payload);
        thunkAPI.dispatch(updatePreferenceAll(payload));
    },
    {
        condition: (_, api) =>
            (api.getState() as IAppState).db.settings.preferencesStatus ===
            "succeeded",
    },
);

// We have to export the reducer as this is added to the state store
export default settingSlice.reducer;
